import { Colors, Icon, IconType } from '@ateams/components';
import React, { ReactElement } from 'react';
import { createUseStyles } from 'react-jss';
import cx from 'classnames';
import { BorderColors } from '@ateams/components';
import { Button, Typography } from '@a_team/ui-components';
import TooltipWrapped from '../../components/TooltipWrapped';

interface ActionsProps {
  openDialog?: () => void;
  onConfirm?: () => void;
  onLinkedinImport?: () => void;
  isImportingLinkedinProfilePicture?: boolean;
  loading?: boolean;
  disabled?: boolean;
  errors?: string[];
  disableSaveButton?: boolean;
  noLinkedInAvailable?: boolean;
  closeModalAndScrollToLinkedInSection?: () => void;
}

const useStyles = createUseStyles({
  '@global': {
    '.tippy-popper': {
      zIndex: '9999999999 !important',
      maxWidth: 270,
    },
  },
  errorContainer: {
    textAlign: 'center',
    marginTop: 40,
  },
  container: {
    marginTop: 40,
    cursor: 'default',
  },
  button: {
    padding: '8px 12px !important',
  },
  buttonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed !important',
    pointerEvents: 'none',
  },
  fakeButtonDisabled: {
    opacity: 0.5,
  },
  uploadButtons: {
    display: 'flex',
    gap: 8,
  },
  uploadButton: {
    border: `1px solid ${BorderColors.light} !important`,
    backgroundColor: '#FFFFFF !important',
    color: '#222222 !important',
    '& [class*="Button"] span': {
      width: '16px !important',
      height: '16px !important',
    },
    '& i': {
      width: '16px !important',
      height: '16px !important',
    },
    '& > span:last-child': {
      display: 'flex',
      alignItems: 'center',
      gap: 4,
    },
  },
  saveButton: {
    marginTop: 12,
    width: '100% !important',
  },
});

const Actions = ({
  openDialog,
  onConfirm,
  onLinkedinImport,
  isImportingLinkedinProfilePicture,
  loading,
  errors,
  disabled,
  disableSaveButton,
  noLinkedInAvailable,
  closeModalAndScrollToLinkedInSection,
}: ActionsProps): ReactElement => {
  const styles = useStyles();

  const errorsExist = errors && errors.length > 0;

  const handleOnLinkedinImport = () => {
    if (noLinkedInAvailable) {
      closeModalAndScrollToLinkedInSection?.();
    } else {
      onLinkedinImport && onLinkedinImport();
    }
  };

  return (
    <>
      {errorsExist && (
        <div className={styles.errorContainer}>
          {errors?.map((error) => {
            return (
              <Typography
                size={14}
                key={error}
                variant={'textSmall'}
                style={{ display: 'block', color: Colors.primary }}
              >
                {error}
              </Typography>
            );
          })}
        </div>
      )}

      <div
        className={styles.container}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <div className={styles.uploadButtons}>
          <Button
            className={cx(styles.button, styles.uploadButton, {
              [styles.buttonDisabled]: disabled,
            })}
            onClick={(e) => openDialog && openDialog()}
            disabled={disabled}
          >
            <Icon type={IconType.UploadDark} />
            Upload new
          </Button>

          <TooltipWrapped
            theme="dark"
            position="top"
            title="To import a profile photo add your LinkedIn URL to your profile."
            animation="fade"
            arrow
            disabled={!noLinkedInAvailable}
          >
            <Button
              className={cx(styles.button, styles.uploadButton, {
                [styles.buttonDisabled]: disabled,
                [styles.fakeButtonDisabled]: noLinkedInAvailable,
              })}
              onClick={() => handleOnLinkedinImport()}
              loading={isImportingLinkedinProfilePicture}
            >
              <Icon type={IconType.LinkedInDarkV2} />
              Import LinkedIn photo
            </Button>
          </TooltipWrapped>
        </div>
        <div className={styles.saveButton}>
          <Button
            className={cx(styles.button, styles.saveButton, {
              [styles.buttonDisabled]: disabled || disableSaveButton,
            })}
            onClick={(e) => onConfirm && onConfirm()}
            loading={loading}
            disabled={disabled || disableSaveButton}
          >
            Save photo
          </Button>
        </div>
      </div>
    </>
  );
};

export default Actions;
